import { useClerk, useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMember } from '../auth/useMember';
import { useCustomerSession } from './CustomerSession';
import { leavesTryOn, sectionOf } from './navigation';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

/**
 * The customer-surface nav: a hamburger over three sections, and the only way
 * into `/admin`.
 *
 * Rendered once by the customer layout route rather than by each screen, so it
 * is identical across garments, capture and try-on, and each screen's own header
 * stays what it always was: a page title beneath it.
 *
 * ⚠️ The admin entry is gated on `member.role === 'admin'`, mirroring
 * `RequireAdmin`. That is a *rendering* decision and never the security
 * boundary — the API enforces the role from the JWT on every request. What the
 * gate buys is that a shop device provisioned with a `user` credential shows a
 * customer no door at all.
 *
 * ⚠️ **Sign out is deliberately not gated with it.** A `user` device otherwise
 * has no way out of a signed-in session anywhere in the app, since the admin
 * shell is the only other place the control exists and a `user` credential
 * cannot reach it.
 */

interface Destination {
  to: string;
  label: string;
  adminOnly?: boolean;
}

const DESTINATIONS: Destination[] = [
  { to: '/', label: 'Garments' },
  { to: '/try-on', label: 'Try on' },
  { to: '/admin', label: 'Admin', adminOnly: true },
];

export function CustomerNav() {
  const member = useMember();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasVisit, endVisit } = useCustomerSession();

  const [menuOpen, setMenuOpen] = useState(false);
  /** The destination a leave-warning is currently asking about. */
  const [leavingTo, setLeavingTo] = useState<string | null>(null);
  /** True while a refresh is waiting on the try-on confirmation below. */
  const [confirmingRefresh, setConfirmingRefresh] = useState(false);

  const isAdmin = member?.role === 'admin';

  /**
   * ⚠️ Read off Clerk's user, **not** off `useMember()`. That hook returns
   * `null` for a device whose `publicMetadata` is incomplete — deliberately, so
   * it matches what the server will do with the token — and taking the username
   * from it meant the label vanished in exactly the state where knowing which
   * credential is signed in matters most.
   */
  const username = user?.username ?? null;

  const currentSection = sectionOf(pathname);
  const destinations = DESTINATIONS.filter((item) => !item.adminOnly || isAdmin);

  // A route change from anywhere (including the leave-warning below) closes the
  // menu, so it never hangs open over the screen it just navigated to.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  /**
   * ⚠️ **The one piece of real logic in this component.** Leaving the try-on
   * section ends the visit — the photo and every generated image are cleared —
   * so it must never happen on a single tap. Anything else navigates directly.
   *
   * Scoped to in-app navigation only. A reload is deliberately *not* warned
   * about: the visit survives it, since the photo key is in `sessionStorage` and
   * the results are in IndexedDB, so there would be nothing to warn about.
   */
  function go(to: string): void {
    setMenuOpen(false);

    if (leavesTryOn(pathname, to, hasVisit)) {
      setLeavingTo(to);
      return;
    }

    // Entering try-on without a photo routes through capture — the photo is what
    // gates that screen, and `TryOnScreen` redirects here anyway.
    if (to === '/try-on' && !hasVisit) {
      void navigate('/capture');
      return;
    }

    void navigate(to);
  }

  /**
   * ⚠️ **Confirmed on try-on only — and not for the reason `go()` warns.**
   *
   * A reload does not *clear* anything: `visitId` and `photoKey` live in
   * `sessionStorage` and every result is in IndexedDB, which is exactly why
   * `go()` says a reload needs no warning. What a reload does destroy is a
   * generation **still in flight** — the request dies with the page, while the
   * quota has already been reserved and Gemini has already been billed for an
   * image that will now never arrive. That is a real cost, it is invisible from
   * here, and it is worth one tap to avoid.
   *
   * ℹ️ **Gated on `hasVisit` for the same reason `leavesTryOn` is** — a try-on
   * screen with no photo has nothing running and must not interrupt anyone.
   * Everywhere else the reload is immediate.
   */
  function requestRefresh(): void {
    setMenuOpen(false);

    if (hasVisit && sectionOf(pathname) === '/try-on') {
      setConfirmingRefresh(true);
      return;
    }

    window.location.reload();
  }

  return (
    /**
     * `pt-safe` sits on this element rather than on the page below it: the bar
     * is sticky, so the notch inset has to be part of what stays pinned, or the
     * status-bar strip goes transparent the moment the grid scrolls under it.
     *
     * z-20 clears the sticky bottom bars on every screen and stays well under
     * `Modal`'s z-50.
     */
    <header className="sticky top-0 z-20 border-b border-(--color-border-subtle) bg-surface pt-safe">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label="Menu"
            className="min-h-(--size-control-md) -ml-2 inline-flex w-11 shrink-0 items-center justify-center rounded-md text-primary active:bg-subtle"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="min-w-0">
            <p className="truncate text-base font-bold text-primary">
              {destinations.find((item) => item.to === currentSection)?.label ?? 'Shringaar'}
            </p>
            {username && (
              <p className="truncate text-xs text-tertiary">Signed in as {username}</p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {/**
           * ⚠️ **A standalone PWA has no address bar, so there is no reload.**
           * On a laptop a stuck screen is one Cmd-R away; on a shop iPad added to
           * the home screen there is no gesture for it at all, and the staff
           * member's only recourse was to force-quit the app. It sits in the bar
           * rather than behind the hamburger because the screens worth reloading
           * are the ones already looking wrong.
           *
           * It lives in `CustomerNav` rather than the admin shell because this
           * component is rendered by *both* layouts — so staff on a `user`
           * credential get it too, which is the point.
           *
           * ℹ️ **A plain reload is enough — nothing needs clearing.** React
           * Query's cache is in memory only, so this drops every stale row; and
           * since `getPublicUrl` stamps `?v=` into image urls (G45), a replaced
           * image can no longer hide behind a url the browser already has.
           */}
          <button
            type="button"
            onClick={requestRefresh}
            className="min-h-(--size-control-md) shrink-0 rounded-md px-3 text-sm font-medium text-secondary active:bg-subtle"
          >
            Refresh
          </button>

          <button
            type="button"
            onClick={() => void signOut()}
            className="min-h-(--size-control-md) shrink-0 rounded-md px-3 text-sm font-medium text-danger-text"
          >
            Sign out
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="mx-auto w-full max-w-6xl px-2 pb-2">
          {destinations.map((item) => {
            const active = item.to === currentSection;
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => go(item.to)}
                aria-current={active ? 'page' : undefined}
                className={[
                  'block min-h-(--size-touch-min) w-full rounded-md px-3 text-left text-base font-medium',
                  active ? 'bg-accent-subtle text-accent-text' : 'text-secondary active:bg-subtle',
                ].join(' ')}
              >
                {item.label}
              </button>
            );
          })}

        </nav>
      )}

      <Modal
        open={leavingTo !== null}
        onClose={() => setLeavingTo(null)}
        title="Leave the try-on session?"
        footer={
          <>
            <Button variant="secondary" size="md" onClick={() => setLeavingTo(null)}>
              Stay
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={() => {
                const to = leavingTo;
                setLeavingTo(null);
                void endVisit().then(() => navigate(to ?? '/', { replace: true }));
              }}
            >
              Leave and clear
            </Button>
          </>
        }
      >
        <p className="text-sm text-secondary">
          Leaving ends this session. The photo and every try-on image will be cleared from this
          device.
        </p>
      </Modal>

      <Modal
        open={confirmingRefresh}
        onClose={() => setConfirmingRefresh(false)}
        title="Refresh now?"
        footer={
          <>
            <Button variant="secondary" size="md" onClick={() => setConfirmingRefresh(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </>
        }
      >
        <p className="text-sm text-secondary">
          A try-on still generating will be lost, and its quota is already used — it is not
          refunded. Images already finished are kept, and the photo stays on this device.
        </p>
      </Modal>
    </header>
  );
}
